'use client'

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type FocusEventHandler,
  type ChangeEventHandler,
} from 'react'

import { CONFIG_GOOGLE_MAPS_BROWSER_KEY } from '@src/config/env'
import { addressGoogleAutocompleteHolder } from '@src/quince-site-common/src/services/addressService/address-google-autocomplete.holder'
import {
  getAddressFromPlaceComponents,
  type GeocoderAddressComponent,
} from '@src/quince-site-common/src/services/addressService/get-address-from-place-components'

import useScript, { SCRIPT_STATES } from '../../../hooks/use-script'
import { Input } from '../../FormElements'

import type { FieldValues, UseFormRegister } from 'react-hook-form'

// eslint-disable-next-line i18next/no-literal-string
export const GOOGLE_PLACES_INTEGRATION_RHF = 'react-hook-form'
// eslint-disable-next-line i18next/no-literal-string
export const GOOGLE_PLACES_INTEGRATION_CONTROLLED = 'controlled'

/** Stable Google Maps script URL (no per-session query params). */
export const getGoogleMapsScriptSrc = (apiKey: string) =>
  // eslint-disable-next-line i18next/no-literal-string
  apiKey ? `https://maps.googleapis.com/maps/api/js?language=en&key=${apiKey}&libraries=places` : ''

// eslint-disable-next-line i18next/no-literal-string
const PLACES_FIELD_KEYS = ['address_components', 'geometry', 'name'] as const

// Google's `places.Autocomplete` widget fires a Places API request on every keystroke (it has only
// a small internal throttle). Wrapping the 'input' listener it attaches lets us coalesce keystrokes
// before Google sees them, cutting Places Autocomplete request volume / cost. Non-'input' listeners
// (keydown for arrow nav, focus/blur) pass through unchanged so dropdown UX is untouched.
const PLACES_INPUT_DEBOUNCE_MS = 300

/**
 * Google Places injects `.pac-container` directly into `<body>`, outside the React tree.
 * These styles must be global (not CSS Modules) to target those elements.
 * Pages Router restricts global CSS imports to `_app` only, so we inject a `<style>` tag
 * at runtime instead of using a plain `.scss`
 */
// eslint-disable-next-line i18next/no-literal-string
const PAC_CONTAINER_SELECTOR = '.pac-container'
// eslint-disable-next-line i18next/no-literal-string
const PAC_ITEM_SELECTOR = '.pac-item'
// eslint-disable-next-line i18next/no-literal-string
const PAC_STYLE_ID = 'google-places-pac-styles'
const PAC_STYLES = `
.pac-item { display:flex; padding-top:1.1rem; border:none; }
.pac-item .pac-icon { display:none; }
.pac-item .pac-matched { color:#21201f; font-family:Grosa,sans-serif; font-size:1.4rem; font-weight:500; line-height:1.9rem; }
.pac-item :last-child { flex:1; padding-bottom:1.1rem; }
.pac-container { padding:0 1.1rem 1.1rem; z-index:100050; }
.pac-container::before { content:'SUGGESTIONS'; display:block; padding:1.1rem 0; }
.pac-container::after { background-image:none!important; display:block; height:2rem; width:12rem; content:' '; }
`

const useInjectPacStyles = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (document.getElementById(PAC_STYLE_ID)) return
    const style = document.createElement('style')
    style.id = PAC_STYLE_ID
    style.textContent = PAC_STYLES
    document.head.appendChild(style)
  }, [])
}

/** Google injects `.pac-container` on `body`; hide all when the user scrolls any scrollable surface. */
function hideGooglePlacesPacContainers() {
  document.querySelectorAll(PAC_CONTAINER_SELECTOR).forEach(node => {
    ;(node as HTMLElement).style.display = 'none'
  })
}

export interface GooglePlacesStateOption {
  stateCode: string
  stateName: string
}

interface PlaceResult {
  address_components?: GeocoderAddressComponent[]
  name?: string
}

interface GoogleAutocompleteInstance {
  addListener: (eventName: string, handler: () => void) => void
  getPlace: () => PlaceResult
}

declare global {
  interface Window {
    google?: {
      maps: {
        event: { clearInstanceListeners: (instance: unknown) => void }
        places: {
          Autocomplete: new (input: HTMLInputElement, options: object) => GoogleAutocompleteInstance
        }
      }
    }
  }
}

export interface GooglePlacesAddressLine1Props {
  integration: typeof GOOGLE_PLACES_INTEGRATION_RHF | typeof GOOGLE_PLACES_INTEGRATION_CONTROLLED
  name: string
  placeholder?: string
  ariaLabel?: string
  register?: UseFormRegister<FieldValues>
  errors?: Record<string, { message: string }>
  required?: boolean
  containerClassName?: string
  errorClassName?: string
  pattern?: RegExp
  patternMessage?: string
  maxLength?: number
  defaultValue?: string
  value?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  className?: string
  stateOptions?: GooglePlacesStateOption[]
  countryCode?: string
  mapsApiKey?: string
  onFilledFromPlaces?: () => void
  applyAutofillFields: (fields: Record<string, string>) => void
  /** Defaults to {@link Input} from FormElements; Account AddressForm passes styled `Input`. */
  InputComponent?: ComponentType<Record<string, unknown>>
}

/**
 * Shared address line 1 field with Google Places Autocomplete.
 * Used by account Form (react-hook-form) and post-order AddressForm (controlled state).
 */
const GooglePlacesAddressLine1 = ({
  integration,
  name,
  placeholder,
  ariaLabel,
  register,
  errors,
  required = true,
  containerClassName,
  errorClassName,
  pattern,
  patternMessage,
  maxLength,
  defaultValue,
  value,
  onChange,
  onBlur,
  className,
  stateOptions = [],
  countryCode = 'us',
  mapsApiKey = CONFIG_GOOGLE_MAPS_BROWSER_KEY,
  onFilledFromPlaces,
  applyAutofillFields,
  InputComponent,
}: GooglePlacesAddressLine1Props) => {
  useInjectPacStyles()
  const InputCmp = (InputComponent ?? Input) as ComponentType<Record<string, unknown>>
  const autoCompleteRef = useRef<GoogleAutocompleteInstance | null>(null)
  const onPlaceChangeRef = useRef<((place: PlaceResult) => void) | null>(null)
  const inputElRef = useRef<HTMLInputElement | null>(null)
  const [inputReady, setInputReady] = useState(false)
  const avoidBlurRef = useRef(false)

  const inputCallbackRef = useCallback((el: HTMLInputElement | null) => {
    inputElRef.current = el
    setInputReady(!!el)
  }, [])

  // eslint-disable-next-line i18next/no-literal-string -- ISO country default
  const selectedCountry = countryCode?.trim().toLowerCase()

  const scriptSrc = getGoogleMapsScriptSrc(mapsApiKey)
  const scriptStatus = useScript(scriptSrc)

  const onPlaceChange = useCallback(
    (place: PlaceResult) => {
      if (!place?.address_components) return
      const { zip, provinceCode, city } = getAddressFromPlaceComponents(place.address_components)
      const line1 = place.name ?? ''
      const stateName = stateOptions.find(s => s.stateCode === provinceCode)?.stateName ?? provinceCode ?? ''

      applyAutofillFields({
        [name]: line1,
        city,
        zip,
        province: stateName,
        address2: '',
      })
      onFilledFromPlaces?.()
    },
    [applyAutofillFields, name, onFilledFromPlaces, stateOptions],
  )

  onPlaceChangeRef.current = onPlaceChange

  useEffect(() => {
    if (autoCompleteRef.current && typeof window !== 'undefined' && window.google?.maps) {
      window.google.maps.event.clearInstanceListeners(autoCompleteRef.current)
      autoCompleteRef.current = null
    }
  }, [selectedCountry])

  useEffect(() => {
    if (
      autoCompleteRef.current != null ||
      scriptStatus !== SCRIPT_STATES.READY ||
      !inputReady ||
      !inputElRef.current ||
      typeof window === 'undefined' ||
      !window.google?.maps?.places
    ) {
      return
    }

    const options = {
      componentRestrictions: { country: selectedCountry },
      fields: [...PLACES_FIELD_KEYS],
    }

    // Wrap the 'input' / 'keydown' / 'keyup' listeners Google attaches so we can debounce keystrokes
    // before Google's widget fires a Places Autocomplete request. The override stays installed for the
    // lifetime of the effect (restored in cleanup) because Google attaches some listeners async via
    // microtasks after construction. Non-typing listeners pass through unchanged. React/RHF read
    // values via their own delegated root listeners (not via this DOM addEventListener), so they're
    // unaffected by the wrap.
    const inputEl = inputElRef.current
    const pendingDebounceTimers = new Set<ReturnType<typeof setTimeout>>()
    let wrappedAtLeastOneTypingListener = false
    const originalAddEventListener = inputEl.addEventListener.bind(inputEl)
    // eslint-disable-next-line i18next/no-literal-string -- DOM event names
    const TYPING_EVENTS = new Set(['input', 'keyup', 'keydown'])
    // eslint-disable-next-line i18next/no-literal-string -- DOM event names (nav keys must pass through immediately so dropdown nav stays snappy)
    const NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'])
    inputEl.addEventListener = ((
      type: string,
      listener: EventListenerOrEventListenerObject,
      eventOptions?: boolean | AddEventListenerOptions,
    ) => {
      if (TYPING_EVENTS.has(type) && typeof listener === 'function') {
        wrappedAtLeastOneTypingListener = true
        // Each wrapped listener owns its own timer. Sharing one timer across input/keydown/keyup
        // would let keyup's schedule cancel input's schedule on every keystroke, silently dropping
        // the listener that drives the Places request.
        let timer: ReturnType<typeof setTimeout> | null = null
        const debouncedListener: EventListener = event => {
          if (event instanceof KeyboardEvent && NAV_KEYS.has(event.key)) {
            listener(event)
            return
          }
          // IME / CJK composition: pass through immediately so Google's listener sees consistent
          // event state. Debouncing during composition risks Google reading a stale `event.data` /
          // `event.inputType` and degrading autocomplete suggestions for international users.
          if ('isComposing' in event && (event as InputEvent | KeyboardEvent).isComposing) {
            listener(event)
            return
          }
          if (timer) {
            clearTimeout(timer)
            pendingDebounceTimers.delete(timer)
          }
          const scheduled = setTimeout(() => {
            pendingDebounceTimers.delete(scheduled)
            timer = null
            listener(event)
          }, PLACES_INPUT_DEBOUNCE_MS)
          timer = scheduled
          pendingDebounceTimers.add(scheduled)
        }
        originalAddEventListener(type, debouncedListener, eventOptions)
        return
      }
      originalAddEventListener(type, listener, eventOptions)
    }) as typeof inputEl.addEventListener

    autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputEl, options)

    // Dev-only canary: if Google's SDK ever stops attaching its typing listeners via
    // addEventListener (e.g. switches to `inputEl.oninput = ...` or a MutationObserver), our override
    // silently no-ops and the only failure signal is a Places billing spike. A delayed check (well
    // past the async/microtask attachment window) surfaces the regression in normal dev logs.
    const canaryTimer = setTimeout(() => {
      if (!wrappedAtLeastOneTypingListener && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console, i18next/no-literal-string
        console.warn(
          '[GooglePlacesAddressLine1] debounce override was not exercised — Google SDK may have changed listener attachment',
        )
      }
    }, 1000)

    const placesSessionId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `places-${Date.now()}-${Math.random().toString(16).slice(2)}`
    addressGoogleAutocompleteHolder.setGoogleAutoCompleteSessionId(placesSessionId)

    autoCompleteRef.current.addListener('place_changed', () => {
      if (!autoCompleteRef.current) return
      const place = autoCompleteRef.current.getPlace()
      onPlaceChangeRef.current?.(place)
    })

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (target instanceof Element && typeof target.closest === 'function' && target.closest(PAC_ITEM_SELECTOR)) {
        avoidBlurRef.current = true
      }
    }

    // `document` in capture phase intercepts scroll from the window *and* every nested scrollable element.
    // `visualViewport` is a separate target for mobile keyboard / pinch-zoom shifts.
    // eslint-disable-next-line i18next/no-literal-string -- DOM API
    document.body.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('scroll', hideGooglePlacesPacContainers, true)
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    vv?.addEventListener('scroll', hideGooglePlacesPacContainers)

    return () => {
      clearTimeout(canaryTimer)
      pendingDebounceTimers.forEach(clearTimeout)
      pendingDebounceTimers.clear()
      // inputEl is non-null per the guard at the top of this effect
      inputEl.addEventListener = originalAddEventListener
      // eslint-disable-next-line i18next/no-literal-string -- DOM API
      document.body.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('scroll', hideGooglePlacesPacContainers, true)
      vv?.removeEventListener('scroll', hideGooglePlacesPacContainers)
    }
  }, [scriptStatus, selectedCountry, inputReady])

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    if (avoidBlurRef.current) {
      avoidBlurRef.current = false
      return
    }
    onBlur?.(e)
  }

  if (integration === GOOGLE_PLACES_INTEGRATION_RHF) {
    return (
      <InputCmp
        placeholder={placeholder}
        ariaLabel={ariaLabel ?? placeholder}
        name={name}
        defaultValue={defaultValue}
        type="text"
        register={register}
        errors={errors}
        required={required}
        containerClassName={containerClassName}
        errorClassName={errorClassName}
        pattern={pattern}
        patternMessage={patternMessage}
        maxLength={maxLength}
        inputRef={inputCallbackRef}
        onBlur={handleBlur}
        // eslint-disable-next-line i18next/no-literal-string -- disable browser autofill
        autoComplete="off"
      />
    )
  }

  return (
    <InputCmp
      className={className}
      placeholder={placeholder}
      ariaLabel={ariaLabel ?? placeholder}
      name={name}
      value={value}
      onChange={onChange}
      type="text"
      errors={errors}
      required={required}
      containerClassName={containerClassName}
      errorClassName={errorClassName}
      pattern={pattern}
      patternMessage={patternMessage}
      maxLength={maxLength}
      inputRef={inputCallbackRef}
      onBlur={handleBlur}
      // eslint-disable-next-line i18next/no-literal-string -- disable browser autofill
      autoComplete="off"
    />
  )
}

export default GooglePlacesAddressLine1
